import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Action,
  Selector,
  State,
  StateContext,
  createSelector,
} from '@ngxs/store';
import { Observable, Subject, Subscription, forkJoin, of, timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';

import { MedicalCase } from '../../../domain/medical-case/model/medical-case';
import { MedicalCaseProcessedContent } from '../../../domain/medical-case/model';
import { MedicalCaseApiService } from '../../../domain/medical-case/service/medical-case-api.service';
import { PipelineTab } from '../../../shared/model/pipeline-tab';
import { MedicalCaseProcessedContentUtil } from '../util/medical-case-processed-content.util';
import {
  buildPreparationTabs,
  derivePreparationStepCompletion,
  hasPreparationFile,
} from '../util/medical-case-pipeline-step.util';
import {
  MedicalCasePipelineFlow,
  INITIAL_STEP_COMPLETION,
  INITIAL_STEP_PROCESSING,
  PreparationStepKey,
  ProcessingStep,
  StepContentState,
  StepCompletionState,
  StepProcessingState,
} from '../model/pipeline-step.model';
import { MedicalCasePipelineStepActions } from './medical-case-pipeline-step.actions';
import { MedicalCasePhaseStatusUtil } from '../util/medical-case-phase-status.util';
import { StepBadgeStatus } from '../util/medical-case-status-resolver.util';

export interface MedicalCasePipelineStepStateModel {
  caseId: string | null;
  flow: MedicalCasePipelineFlow;
  caseDetail: MedicalCase | null;
  stepCompletion: StepCompletionState;
  stepProcessing: StepProcessingState;
  stepContent: StepContentState;
  stepContentFileSignatures: Partial<Record<PreparationStepKey, string>>;
  processingStatuses: Partial<
    Record<PreparationStepKey, StepBadgeStatus>
  >;
  submittedOriginalDocumentIdsByStep: Partial<
    Record<PreparationStepKey, string[]>
  >;
  backendProcessing: boolean;
  processingStep: ProcessingStep;
  processingError: string | null;
  pollingActive: boolean;
  pollingSessionId: number;
  initialized: boolean;
}

const INITIAL_STATE: MedicalCasePipelineStepStateModel = {
  caseId: null,
  flow: 'preparation',
  caseDetail: null,
  stepCompletion: { ...INITIAL_STEP_COMPLETION },
  stepProcessing: { ...INITIAL_STEP_PROCESSING },
  stepContent: {},
  stepContentFileSignatures: {},
  processingStatuses: {},
  submittedOriginalDocumentIdsByStep: {},
  backendProcessing: false,
  processingStep: null,
  processingError: null,
  pollingActive: false,
  pollingSessionId: 0,
  initialized: false,
};

@State<MedicalCasePipelineStepStateModel>({
  name: 'medicalCasePipelineStep',
  defaults: INITIAL_STATE,
})
@Injectable()
export class MedicalCasePipelineStepState {
  private readonly api = inject(MedicalCaseApiService);
  private readonly destroyRef = inject(DestroyRef);
  private static readonly POLLING_INTERVAL_MS = 5000;
  private pollingStop$?: Subject<void>;
  private pollingSubscription?: Subscription;

  @Selector()
  static caseDetail(
    state: MedicalCasePipelineStepStateModel
  ): MedicalCase | null {
    return state.caseDetail;
  }

  @Selector()
  static stepCompletion(
    state: MedicalCasePipelineStepStateModel
  ): StepCompletionState {
    return state.stepCompletion;
  }

  @Selector()
  static backendProcessing(state: MedicalCasePipelineStepStateModel): boolean {
    return state.backendProcessing;
  }

  @Selector()
  static stepProcessing(
    state: MedicalCasePipelineStepStateModel
  ): StepProcessingState {
    return state.stepProcessing;
  }

  @Selector()
  static stepContent(
    state: MedicalCasePipelineStepStateModel
  ): StepContentState {
    return state.stepContent;
  }

  @Selector()
  static processingStep(
    state: MedicalCasePipelineStepStateModel
  ): ProcessingStep {
    return state.processingStep;
  }

  @Selector()
  static processingError(
    state: MedicalCasePipelineStepStateModel
  ): string | null {
    return state.processingError;
  }

  @Selector()
  static submittedOriginalDocumentIdsByStepMap(
    state: MedicalCasePipelineStepStateModel
  ): Partial<Record<PreparationStepKey, string[]>> {
    return state.submittedOriginalDocumentIdsByStep;
  }

  @Selector()
  static tabs(state: MedicalCasePipelineStepStateModel): PipelineTab[] {
    return buildPreparationTabs(
      state.flow,
      state.stepCompletion,
      state.stepProcessing,
      state.caseDetail?.phases
    );
  }

  @Selector()
  static initialized(state: MedicalCasePipelineStepStateModel): boolean {
    return state.initialized;
  }

  static initializedForCase(caseId: string) {
    return createSelector(
      [MedicalCasePipelineStepState],
      (state: MedicalCasePipelineStepStateModel) =>
        state.initialized && state.caseId === caseId
    );
  }

  static canProceedFrom(step: PreparationStepKey) {
    return createSelector(
      [MedicalCasePipelineStepState],
      (state: MedicalCasePipelineStepStateModel) =>
        step === 'upload' || Boolean(state.stepCompletion[step])
    );
  }

  static hasFileId(step: PreparationStepKey) {
    return createSelector(
      [MedicalCasePipelineStepState],
      (state: MedicalCasePipelineStepStateModel) =>
        hasPreparationFile(state.flow, state.caseDetail, step)
    );
  }

  static stepProcessingByStep(step: PreparationStepKey) {
    return createSelector(
      [MedicalCasePipelineStepState],
      (state: MedicalCasePipelineStepStateModel) => state.stepProcessing[step]
    );
  }

  static stepContentByStep(step: PreparationStepKey) {
    return createSelector(
      [MedicalCasePipelineStepState],
      (state: MedicalCasePipelineStepStateModel) =>
        state.stepContent[step] ?? null
    );
  }

  static submittedOriginalDocumentIdsByStep(step: PreparationStepKey) {
    return createSelector(
      [MedicalCasePipelineStepState],
      (state: MedicalCasePipelineStepStateModel) =>
        state.submittedOriginalDocumentIdsByStep[step] ?? []
    );
  }

  @Action(MedicalCasePipelineStepActions.Initialize)
  initialize(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { caseId, initialCaseDetail, flow }: MedicalCasePipelineStepActions.Initialize
  ): void {
    if (initialCaseDetail != null) {
      const stepCompletion = this.resolveStepCompletion(flow, initialCaseDetail);

      ctx.patchState({
        ...INITIAL_STATE,
        caseId,
        flow,
        caseDetail: initialCaseDetail,
        stepCompletion,
        initialized: true,
      });
    } else {
      ctx.patchState({
        ...INITIAL_STATE,
        caseId,
        flow,
        initialized: true,
      });
    }
  }

  @Action(MedicalCasePipelineStepActions.StartPolling)
  startPolling(
    ctx: StateContext<MedicalCasePipelineStepStateModel>
  ): Observable<MedicalCase | null> {
    const currentState = ctx.getState();
    if (currentState.pollingActive) {
      return of(null);
    }

    this.stopPollingStream();

    const pollingSessionId = currentState.pollingSessionId + 1;
    const pollingStop$ = new Subject<void>();

    this.pollingStop$ = pollingStop$;

    ctx.patchState({ pollingActive: true, pollingSessionId });

    const caseId = ctx.getState().caseId;
    if (caseId == null) {
      this.stopPollingStream();
      ctx.patchState({ pollingActive: false });
      return of(null);
    }

    const polling$ = timer(0, MedicalCasePipelineStepState.POLLING_INTERVAL_MS).pipe(
      takeWhile(() => {
        const state = ctx.getState();
        return state.pollingActive && state.pollingSessionId === pollingSessionId;
      }),
      switchMap(() =>
        this.api.getProcessingCaseSnapshot(caseId).pipe(
          catchError(error => {
            ctx.dispatch(
              new MedicalCasePipelineStepActions.SetProcessingError(
                error?.message ??
                  'Failed to fetch medical case processing phases'
              )
            );
            return of(null);
          })
        )
      ),
      tap(medicalCase => {
        if (medicalCase != null) {
          ctx.dispatch(
            new MedicalCasePipelineStepActions.UpdateProcessingStatuses(
              medicalCase
            )
          );
        }
      }),
      takeUntil(pollingStop$),
      takeUntilDestroyed(this.destroyRef)
    );

    this.pollingSubscription = polling$.subscribe();

    return of(null);
  }

  @Action(MedicalCasePipelineStepActions.StopPolling)
  stopPolling(ctx: StateContext<MedicalCasePipelineStepStateModel>): void {
    this.stopPollingStream();

    ctx.patchState({
      pollingActive: false,
      pollingSessionId: ctx.getState().pollingSessionId + 1,
    });
  }

  @Action(MedicalCasePipelineStepActions.MarkStepCompleted)
  markStepCompleted(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { step }: MedicalCasePipelineStepActions.MarkStepCompleted
  ): void {
    const state = ctx.getState();
    if (!(step in state.stepCompletion)) {
      return;
    }

    ctx.patchState({
      stepCompletion: {
        ...state.stepCompletion,
        [step]: true,
      },
    });
  }

  @Action(MedicalCasePipelineStepActions.ResetStep)
  resetStep(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { step }: MedicalCasePipelineStepActions.ResetStep
  ): void {
    const state = ctx.getState();
    if (!(step in state.stepCompletion)) {
      return;
    }

    ctx.patchState({
      stepCompletion: {
        ...state.stepCompletion,
        [step]: false,
      },
    });
  }

  @Action(MedicalCasePipelineStepActions.SetBackendProcessing)
  setBackendProcessing(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { processing }: MedicalCasePipelineStepActions.SetBackendProcessing
  ): void {
    ctx.patchState({
      backendProcessing: processing,
      processingError: processing ? null : ctx.getState().processingError,
    });
  }

  @Action(MedicalCasePipelineStepActions.SetProcessingError)
  setProcessingError(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { error }: MedicalCasePipelineStepActions.SetProcessingError
  ): void {
    const processingStep = this.resolvePrimaryProcessingStep(
      ctx.getState().stepProcessing
    );
    ctx.patchState({
      backendProcessing: processingStep != null,
      processingStep,
      processingError: error,
    });
  }

  @Action(MedicalCasePipelineStepActions.SetProcessingStep)
  setProcessingStep(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { step }: MedicalCasePipelineStepActions.SetProcessingStep
  ): void {
    ctx.patchState({
      processingStep: step,
    });
  }

  @Action(MedicalCasePipelineStepActions.SetStepProcessing)
  setStepProcessing(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { step, status }: MedicalCasePipelineStepActions.SetStepProcessing
  ): void {
    const state = ctx.getState();
    const stepProcessing = {
      ...state.stepProcessing,
      [step]: status,
    };
    const processingStep = this.resolvePrimaryProcessingStep(stepProcessing);
    const stepContent = { ...state.stepContent };
    const stepContentFileSignatures = { ...state.stepContentFileSignatures };

    if (status === 'processing') {
      delete stepContent[step];
      delete stepContentFileSignatures[step];
    }

    const stepCompletion = { ...state.stepCompletion };
    if (status === 'completed') {
      stepCompletion[step] = true;
    }

    const submittedOriginalDocumentIdsByStep = {
      ...state.submittedOriginalDocumentIdsByStep,
    };
    if (status !== 'processing') {
      delete submittedOriginalDocumentIdsByStep[step];
    }

    ctx.patchState({
      stepProcessing,
      stepCompletion,
      stepContent,
      stepContentFileSignatures,
      submittedOriginalDocumentIdsByStep,
      processingStep,
      backendProcessing: processingStep != null,
    });
  }

  @Action(MedicalCasePipelineStepActions.SetSubmittedOriginalDocumentIds)
  setSubmittedOriginalDocumentIds(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    {
      step,
      originalDocumentIds,
    }: MedicalCasePipelineStepActions.SetSubmittedOriginalDocumentIds
  ): void {
    const state = ctx.getState();
    const sanitizedOriginalDocumentIds = Array.from(
      new Set(
        originalDocumentIds
          .map(originalDocumentId => originalDocumentId.trim())
          .filter(originalDocumentId => originalDocumentId.length > 0)
      )
    );

    const submittedOriginalDocumentIdsByStep = {
      ...state.submittedOriginalDocumentIdsByStep,
      [step]: sanitizedOriginalDocumentIds,
    };

    ctx.patchState({
      submittedOriginalDocumentIdsByStep,
    });
  }

  @Action(MedicalCasePipelineStepActions.UpdateProcessingStatuses)
  updateProcessingStatuses(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { medicalCase }: MedicalCasePipelineStepActions.UpdateProcessingStatuses
  ): Observable<unknown> | void {
    const state = ctx.getState();
    const nextStepProcessing: StepProcessingState = { ...state.stepProcessing };
    const nextStepCompletion = this.resolveStepCompletion(state.flow, medicalCase);
    const phaseStatuses =
      MedicalCasePhaseStatusUtil.getStepStatusesFromPhases(
        medicalCase.phases,
        state.flow
      );
    const nextStepContent: StepContentState = { ...state.stepContent };
    const nextStepContentFileSignatures = {
      ...state.stepContentFileSignatures,
    };
    const nextSubmittedOriginalDocumentIdsByStep = {
      ...state.submittedOriginalDocumentIdsByStep,
    };
    let nextProcessingError: string | null = null;

    for (const [step, status] of Object.entries(phaseStatuses) as [
      PreparationStepKey,
      StepBadgeStatus,
    ][]) {
      if (status.status === 'processing') {
        nextStepProcessing[step] = 'processing';
        delete nextStepContent[step];
        delete nextStepContentFileSignatures[step];
        continue;
      }

      if (status.status === 'error') {
        nextStepProcessing[step] = 'error';
        delete nextStepContent[step];
        delete nextStepContentFileSignatures[step];
        delete nextSubmittedOriginalDocumentIdsByStep[step];
        nextProcessingError = 'One or more processing phases failed.';
        continue;
      }

      if (status.status !== 'completed') {
        nextStepProcessing[step] = 'idle';
        delete nextStepContent[step];
        delete nextStepContentFileSignatures[step];
        delete nextSubmittedOriginalDocumentIdsByStep[step];
        continue;
      }

      nextStepProcessing[step] = 'completed';
      delete nextSubmittedOriginalDocumentIdsByStep[step];
      const fileIds = Array.from(new Set(status.fileIds));
      const fileSignature = this.buildFileSignature(fileIds);

      if (fileIds.length === 0) {
        delete nextStepContent[step];
        delete nextStepContentFileSignatures[step];
      } else {
        nextStepContentFileSignatures[step] = fileSignature;
      }
    }

    const processingStep =
      this.resolvePrimaryProcessingStep(nextStepProcessing);
    const backendProcessing = processingStep != null;

    ctx.patchState({
      caseDetail: medicalCase,
      processingStatuses: phaseStatuses,
      stepProcessing: nextStepProcessing,
      stepCompletion: nextStepCompletion,
      stepContent: nextStepContent,
      stepContentFileSignatures: nextStepContentFileSignatures,
      submittedOriginalDocumentIdsByStep:
        nextSubmittedOriginalDocumentIdsByStep,
      processingStep,
      backendProcessing,
      processingError: nextProcessingError,
    });
  }

  @Action(MedicalCasePipelineStepActions.FetchStepContent)
  fetchStepContent(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { step, fileIds }: MedicalCasePipelineStepActions.FetchStepContent
  ): Observable<unknown> {
    const caseId = ctx.getState().caseId;
    if (caseId == null || fileIds.length === 0) {
      return of(null);
    }

    const uniqueFileIds = Array.from(new Set(fileIds));
    const requests = uniqueFileIds.map(fileId =>
      this.api.getProcessingResult(caseId, fileId)
    );

    return forkJoin(requests).pipe(
      map(contents => this.mergeProcessingContents(contents)),
      tap(content => {
        ctx.dispatch(
          new MedicalCasePipelineStepActions.StoreStepContent(step, content)
        );
      }),
      catchError(error => {
        ctx.dispatch([
          new MedicalCasePipelineStepActions.SetStepProcessing(step, 'error'),
          new MedicalCasePipelineStepActions.SetProcessingError(
            error?.message ?? 'Failed to fetch processing result'
          ),
        ]);
        return of(null);
      })
    );
  }

  @Action(MedicalCasePipelineStepActions.StoreStepContent)
  storeStepContent(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { step, content }: MedicalCasePipelineStepActions.StoreStepContent
  ): void {
    const state = ctx.getState();
    const stepContent: StepContentState = {
      ...state.stepContent,
      [step]: content,
    };
    const stepProcessing: StepProcessingState = {
      ...state.stepProcessing,
      [step]: 'completed',
    };
    const processingStep = this.resolvePrimaryProcessingStep(stepProcessing);

    ctx.patchState({
      stepContent,
      stepProcessing,
      processingStep,
      backendProcessing: processingStep != null,
    });
  }

  @Action(MedicalCasePipelineStepActions.UpdateFromBackend)
  updateFromBackend(
    ctx: StateContext<MedicalCasePipelineStepStateModel>,
    { medicalCase }: MedicalCasePipelineStepActions.UpdateFromBackend
  ): Observable<unknown> {
    return ctx.dispatch(
      new MedicalCasePipelineStepActions.UpdateProcessingStatuses(medicalCase)
    );
  }

  @Action(MedicalCasePipelineStepActions.ResetState)
  resetState(ctx: StateContext<MedicalCasePipelineStepStateModel>): void {
    this.stopPollingStream();

    ctx.setState({
      ...INITIAL_STATE,
      stepCompletion: { ...INITIAL_STEP_COMPLETION },
    });
  }

  private resolvePrimaryProcessingStep(
    stepProcessing: StepProcessingState
  ): ProcessingStep {
    return (
      (Object.entries(stepProcessing).find(
        ([, status]) => status === 'processing'
      )?.[0] as ProcessingStep | undefined) ?? null
    );
  }

  private mergeProcessingContents(
    contents: MedicalCaseProcessedContent[]
  ): MedicalCaseProcessedContent {
    if (contents.length === 1) {
      return contents[0];
    }

    if (contents.every(content => typeof content === 'string')) {
      return contents.join('\n\n');
    }

    if (contents.every(content => typeof content !== 'string')) {
      return contents.flatMap(content => content);
    }

    return contents
      .map(content =>
        typeof content === 'string'
          ? content
          : MedicalCaseProcessedContentUtil.toText(content)
      )
      .join('\n\n');
  }

  private buildFileSignature(fileIds: string[]): string {
    return Array.from(new Set(fileIds))
      .map(fileId => fileId.trim())
      .filter(fileId => fileId.length > 0)
      .sort()
      .join('|');
  }

  private stopPollingStream(): void {
    this.pollingStop$?.next();
    this.pollingStop$?.complete();
    this.pollingStop$ = undefined;

    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
  }

  private resolveStepCompletion(
    flow: MedicalCasePipelineFlow,
    medicalCase: MedicalCase
  ): StepCompletionState {
    return derivePreparationStepCompletion(flow, medicalCase);
  }
}
